import './CompetitorSourcesSection.scss';
import React, { useCallback, useMemo, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import useScrollFade from '../../../../shared/hooks/scroll/UseScrollFade';
import PieChart from '../../../../shared/components/chart/pie-chart/PieChart';
import { PieChartData } from '../../../../shared/models/common/PieChartData';
import { DomainsStatsResponse } from '../../../../shared/models/monitor/data/sources/DomainsStatsResponse';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { isCompetitorDomain } from '../../../../shared/helpers/competitor/CompetitorHelper';

interface CompetitorEntry {
  name: string;
  domain?: string;
}

interface KeywordData {
  normalizedKeyword: string;
}

interface CompetitorSourcesSectionProps {
  allDomainsData: DomainsStatsResponse | undefined;
  projectDomain: string | null;
  projectWebsite: string | null;
  competitorKeywords: KeywordData[];
  competition: CompetitorEntry[];
  isLoading: boolean;
  domainClassification: Record<string, string> | null;
  onUpdateDomainClassification: (classification: Record<string, string>) => void;
  isSuperAdmin: boolean;
}

interface ClassifiedDomain {
  domain: string;
  count: number;
  share: number;
}

interface ClassifiedData {
  own: ClassifiedDomain[];
  competitors: ClassifiedDomain[];
  nonCompetitors: ClassifiedDomain[];
  totals: { own: number; competitor: number; nonCompetitor: number };
}

const COMPETITOR_COLORS = {
  own: '#45a987',
  competitor: '#cd3d44',
  nonCompetitor: '#C7C4C4',
};

const extractHostname = (str: string): string => {
  try {
    if (str.startsWith('http://') || str.startsWith('https://')) {
      return new URL(str).hostname.replace(/^www\./, '');
    }
    return str.replace(/^www\./, '').split('/')[0].toLowerCase();
  } catch {
    return str.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].toLowerCase();
  }
};

const getDomainRoot = (domain: string): string => {
  const parts = domain.split('.');
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
};

const isOwnDomain = (domain: string, ownDomains: string[]): boolean => {
  const domainLower = domain.toLowerCase();
  const domainRoot = getDomainRoot(domainLower);

  return ownDomains.some((own) => {
    if (domainLower === own || domainLower.endsWith(`.${own}`)) return true;
    return domainRoot === getDomainRoot(own);
  });
};

const classifyDomains = (
  domainsData: DomainsStatsResponse | undefined,
  ownDomains: string[],
  competitorKeywords: KeywordData[],
  competition: CompetitorEntry[],
  domainClassification: Record<string, string> | null
): ClassifiedData => {
  const empty: ClassifiedData = {
    own: [],
    competitors: [],
    nonCompetitors: [],
    totals: { own: 0, competitor: 0, nonCompetitor: 0 },
  };

  if (!domainsData?.domains?.length) return empty;

  const totalCount = domainsData.domains.reduce((sum, d) => sum + d.count, 0);
  if (totalCount === 0) return empty;

  const own: ClassifiedDomain[] = [];
  const competitors: ClassifiedDomain[] = [];
  const nonCompetitors: ClassifiedDomain[] = [];

  for (const d of domainsData.domains) {
    const share = Math.round((d.count / totalCount) * 10000) / 100;
    const item: ClassifiedDomain = { domain: d.domain, count: d.count, share };

    // Check manual classification override first
    const override = domainClassification?.[d.domain];
    if (override === 'OWN') {
      own.push(item);
      continue;
    } else if (override === 'COMPETITOR') {
      competitors.push(item);
      continue;
    } else if (override === 'NON_COMPETITOR') {
      nonCompetitors.push(item);
      continue;
    }

    if (isOwnDomain(d.domain, ownDomains)) {
      own.push(item);
    } else if (isCompetitorDomain(d.domain, competitorKeywords, competition)) {
      competitors.push(item);
    } else {
      nonCompetitors.push(item);
    }
  }

  // Ensure own domains always appear, even with 0 citations
  const presentOwnDomains = new Set(own.map((d) => d.domain.toLowerCase()));
  for (const od of ownDomains) {
    if (!presentOwnDomains.has(od)) {
      own.push({ domain: od, count: 0, share: 0 });
    }
  }

  const ownTotal = own.reduce((s, d) => s + d.count, 0);
  const compTotal = competitors.reduce((s, d) => s + d.count, 0);
  const nonCompTotal = nonCompetitors.reduce((s, d) => s + d.count, 0);

  return {
    own: own.sort((a, b) => b.count - a.count),
    competitors: competitors.sort((a, b) => b.count - a.count),
    nonCompetitors: nonCompetitors.sort((a, b) => b.count - a.count),
    totals: {
      own: Math.round((ownTotal / totalCount) * 10000) / 100,
      competitor: Math.round((compTotal / totalCount) * 10000) / 100,
      nonCompetitor: Math.round((nonCompTotal / totalCount) * 10000) / 100,
    },
  };
};

const VISIBLE_ROWS = 6;
const ROW_HEIGHT = 28;
const MAX_HEIGHT = VISIBLE_ROWS * ROW_HEIGHT;

interface DomainColumnProps {
  label: string;
  color: string;
  domains: ClassifiedDomain[];
  emptyText: string;
  highlight?: boolean;
  columnType: 'OWN' | 'COMPETITOR' | 'NON_COMPETITOR';
  isSuperAdmin?: boolean;
  onDomainDrop?: (domain: string, targetColumn: string) => void;
  overriddenDomains?: Set<string>;
}

const DomainColumn: React.FC<DomainColumnProps> = ({
  label,
  color,
  domains,
  emptyText,
  highlight,
  columnType,
  isSuperAdmin,
  onDomainDrop,
  overriddenDomains,
}) => {
  const { scrollRef, isAtTop, isAtBottom, handleScroll } = useScrollFade();
  const [isDragOver, setIsDragOver] = useState(false);

  const needsScroll = domains.length > VISIBLE_ROWS;

  return (
    <div
      className={`competitor-sources-section__column${
        isDragOver && isSuperAdmin ? ' competitor-sources-section__column--drag-over' : ''
      }`}
      onDragOver={
        isSuperAdmin
          ? (e) => {
              e.preventDefault();
              setIsDragOver(true);
            }
          : undefined
      }
      onDragLeave={isSuperAdmin ? () => setIsDragOver(false) : undefined}
      onDrop={
        isSuperAdmin
          ? (e) => {
              e.preventDefault();
              setIsDragOver(false);
              const domain = e.dataTransfer.getData('text/plain');
              const sourceColumn = e.dataTransfer.getData('application/x-column-type');
              if (domain && sourceColumn !== columnType) {
                onDomainDrop?.(domain, columnType);
              }
            }
          : undefined
      }
    >
      <div className="competitor-sources-section__column-header">
        <span className="competitor-sources-section__dot" style={{ backgroundColor: color }} />
        {label}
        {domains.length > 0 && (
          <span className="competitor-sources-section__column-count">({domains.length})</span>
        )}
      </div>
      {domains.length === 0 ? (
        <div className="competitor-sources-section__empty">{emptyText}</div>
      ) : (
        <div
          className={`competitor-sources-section__scroll-wrap${
            needsScroll && !isAtTop ? ' competitor-sources-section__scroll-wrap--shadow-top' : ''
          }${needsScroll && !isAtBottom ? ' competitor-sources-section__scroll-wrap--shadow-bottom' : ''}`}
        >
          <SimpleBar
            style={{ maxHeight: `${MAX_HEIGHT}px` }}
            autoHide={false}
            scrollableNodeProps={{ ref: scrollRef, onScroll: handleScroll }}
          >
            <div className="competitor-sources-section__domain-list">
              {domains.map((d) => (
                <div
                  key={d.domain}
                  className={`competitor-sources-section__domain-row${
                    highlight ? ' competitor-sources-section__domain-row--highlight' : ''
                  }${isSuperAdmin ? ' competitor-sources-section__domain-row--draggable' : ''}${
                    isSuperAdmin && overriddenDomains?.has(d.domain)
                      ? ' competitor-sources-section__domain-row--overridden'
                      : ''
                  }`}
                  draggable={isSuperAdmin}
                  onDragStart={
                    isSuperAdmin
                      ? (e) => {
                          e.dataTransfer.setData('text/plain', d.domain);
                          e.dataTransfer.setData('application/x-column-type', columnType);
                          e.dataTransfer.effectAllowed = 'move';
                        }
                      : undefined
                  }
                >
                  <div
                    className="competitor-sources-section__favicon"
                    style={{
                      backgroundImage: `url(https://www.google.com/s2/favicons?domain=${d.domain}&sz=32)`,
                    }}
                  />
                  <span className="competitor-sources-section__domain-name">{d.domain}</span>
                  <span className="competitor-sources-section__domain-share">{d.share}%</span>
                </div>
              ))}
            </div>
          </SimpleBar>
        </div>
      )}
    </div>
  );
};

const CompetitorSourcesSection: React.FC<CompetitorSourcesSectionProps> = ({
  allDomainsData,
  projectDomain,
  projectWebsite,
  competitorKeywords,
  competition,
  isLoading,
  domainClassification,
  onUpdateDomainClassification,
  isSuperAdmin,
}) => {
  const { t } = useTranslation();

  const ownDomains = useMemo(() => {
    const domains: string[] = [];
    if (projectDomain) domains.push(projectDomain.toLowerCase());
    if (projectWebsite) {
      const websiteHost = extractHostname(projectWebsite).toLowerCase();
      if (websiteHost && !domains.includes(websiteHost)) domains.push(websiteHost);
    }
    return domains;
  }, [projectDomain, projectWebsite]);

  const classified = useMemo(
    () => classifyDomains(allDomainsData, ownDomains, competitorKeywords, competition, domainClassification),
    [allDomainsData, ownDomains, competitorKeywords, competition, domainClassification]
  );

  const overriddenDomains = useMemo(
    () => new Set(Object.keys(domainClassification || {})),
    [domainClassification]
  );

  const handleDomainDrop = useCallback(
    (domain: string, targetColumn: string) => {
      const newClassification = { ...(domainClassification || {}), [domain]: targetColumn };
      onUpdateDomainClassification(newClassification);
    },
    [domainClassification, onUpdateDomainClassification]
  );

  const chartData: PieChartData[] = useMemo(
    () => [
      { name: t('yourDomains'), value: classified.totals.own },
      { name: t('competitors'), value: classified.totals.competitor },
      { name: t('nonCompetitors'), value: classified.totals.nonCompetitor },
    ],
    [classified.totals, t]
  );

  const hasData = allDomainsData?.domains && allDomainsData.domains.length > 0;

  return (
    <div className="stats__panel competitor-sources-section">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('competitorVsNonCompetitorSources')}
          <CustomTooltip tooltipText="competitorSourcesTooltip" addMargin={false} />
        </div>
      </div>

      {isLoading ? (
        <div className="stats-box">
          <div className="competitor-sources-section__content">
            <div className="competitor-sources-section__skeleton-chart" />
            <div className="competitor-sources-section__columns">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="competitor-sources-section__skeleton-column">
                  {Array.from({ length: 4 }).map((_, j) => (
                    <div key={j} className="competitor-sources-section__skeleton-row" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : !hasData ? (
        <div className="stats-box stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box">
          <div className="competitor-sources-section__content">
            <div className="competitor-sources-section__chart">
              <PieChart
                chartData={chartData}
                colorPalette={[COMPETITOR_COLORS.own, COMPETITOR_COLORS.competitor, COMPETITOR_COLORS.nonCompetitor]}
                size="SM"
              />
              <div className="competitor-sources-section__chart-center">
                <span className="competitor-sources-section__chart-value" style={{ color: COMPETITOR_COLORS.own }}>
                  {classified.totals.own}%
                </span>
                <span className="competitor-sources-section__chart-label" style={{ color: COMPETITOR_COLORS.own }}>
                  {t('yourDomains')}
                </span>
              </div>
            </div>

            <div className="competitor-sources-section__columns">
              <DomainColumn
                label={t('yourDomains')}
                color={COMPETITOR_COLORS.own}
                domains={classified.own}
                emptyText={t('emptyOwnDomains')}
                highlight
                columnType="OWN"
                isSuperAdmin={isSuperAdmin}
                onDomainDrop={handleDomainDrop}
                overriddenDomains={overriddenDomains}
              />
              <DomainColumn
                label={t('competitors')}
                color={COMPETITOR_COLORS.competitor}
                domains={classified.competitors}
                emptyText={t('emptyCompetitors')}
                columnType="COMPETITOR"
                isSuperAdmin={isSuperAdmin}
                onDomainDrop={handleDomainDrop}
                overriddenDomains={overriddenDomains}
              />
              <DomainColumn
                label={t('nonCompetitors')}
                color={COMPETITOR_COLORS.nonCompetitor}
                domains={classified.nonCompetitors}
                emptyText={t('emptyNonCompetitors')}
                columnType="NON_COMPETITOR"
                isSuperAdmin={isSuperAdmin}
                onDomainDrop={handleDomainDrop}
                overriddenDomains={overriddenDomains}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorSourcesSection;
